import type { MatchingProblemTag, MatchingStyleTag } from "@/types/matching";

export const MATCHING_VERSION = "matching-v1";

export const PROBLEM_TAGS: MatchingProblemTag[] = [
  "anxiety",
  "depression",
  "burnout",
  "trauma",
  "relationships",
  "family",
  "self_esteem",
  "grief",
  "parenting",
  "addiction",
  "sexual_problems",
  "gender_identity",
  "infidelity",
  "friendships",
  "communication",
  "uncertainty",
];

/**
 * Canonical, title-cased labels for a problem tag. Single source of truth for
 * the homepage topic tiles and the `/therapists` filter chip, so the same topic
 * is never named two different ways across the public site.
 */
export const PROBLEM_TAG_LABELS: Record<MatchingProblemTag, string> = {
  anxiety: "Anksioznost",
  depression: "Depresivnost",
  burnout: "Stres i burnout",
  trauma: "Trauma",
  relationships: "Partnerski odnosi",
  family: "Porodični odnosi",
  self_esteem: "Samopouzdanje",
  grief: "Tuga i gubitak",
  parenting: "Roditeljstvo",
  addiction: "Zavisnosti",
  sexual_problems: "Seksualni problemi",
  gender_identity: "Rodni identitet",
  infidelity: "Prevara u odnosu",
  friendships: "Prijateljski odnosi",
  communication: "Komunikacija",
  uncertainty: "Opšta podrška",
};

export function isProblemTag(value: unknown): value is MatchingProblemTag {
  return typeof value === "string" && (PROBLEM_TAGS as string[]).includes(value);
}

export const STYLE_TAGS: MatchingStyleTag[] = [
  "structured",
  "practical",
  "supportive",
  "exploratory",
  "emotional_depth",
  "homework",
];

const SPECIALIZATION_PROBLEM_MAP: Array<{
  pattern: RegExp;
  tags: MatchingProblemTag[];
}> = [
  { pattern: /kognitivno|kbt|cbt|bihejvioral/i, tags: ["anxiety", "depression", "burnout"] },
  { pattern: /gestalt|geštalt/i, tags: ["self_esteem", "relationships"] },
  { pattern: /sistemsk|porodi/i, tags: ["family", "relationships", "parenting"] },
  { pattern: /emdr|traum/i, tags: ["trauma"] },
  { pattern: /adolescent|dje[cč]ij|djec/i, tags: ["parenting", "family"] },
  { pattern: /savjetovan|psiholo/i, tags: ["uncertainty", "self_esteem", "burnout"] },
  { pattern: /seksu|seksol/i, tags: ["sexual_problems", "relationships"] },
];

export function problemTagsFromSpecializations(specializations: string[]): MatchingProblemTag[] {
  const tags = new Set<MatchingProblemTag>();
  for (const specialization of specializations) {
    for (const entry of SPECIALIZATION_PROBLEM_MAP) {
      if (!entry.pattern.test(specialization)) continue;
      for (const tag of entry.tags) tags.add(tag);
    }
  }
  return [...tags];
}

export function problemLabel(tag: MatchingProblemTag): string {
  switch (tag) {
    case "anxiety":
      return "anksioznost";
    case "depression":
      return "depresivnost";
    case "burnout":
      return "stres i burnout";
    case "trauma":
      return "trauma";
    case "relationships":
      return "partnerski odnosi";
    case "family":
      return "porodični odnosi";
    case "self_esteem":
      return "samopouzdanje";
    case "grief":
      return "tuga i gubitak";
    case "parenting":
      return "roditeljstvo";
    case "addiction":
      return "zavisnosti";
    case "sexual_problems":
      return "seksualni problemi";
    case "gender_identity":
      return "rodni identitet";
    case "infidelity":
      return "prevara u odnosu";
    case "friendships":
      return "prijateljski odnosi";
    case "communication":
      return "komunikacija";
    case "uncertainty":
      return "opšta podrška";
  }
}
